const _unread = (root) => (root?.notifications || []).filter(n => !n.read).length

const ICON_BY_TYPE = {
  mention: 'chat',
  ticket_assign: 'check',
  calendar_invite: 'date',
  doc_share: 'document',
  system: 'settings'
}

const COLOR_BY_TYPE = {
  mention: 'blue',
  ticket_assign: 'green',
  calendar_invite: 'purple',
  doc_share: 'orange',
  system: 'caption'
}

const _timeAgo = (iso) => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  return `${days}d`
}

export const NotificationBell = {
  tag: 'button',
  type: 'button',
  title: 'Notifications',
  'aria-label': 'Notifications',
  position: 'relative',
  padding: 'Z',
  round: 'Z',
  cursor: 'pointer',
  borderWidth: '0',
  background: 'transparent',
  color: 'caption',
  ':hover': { background: 'overlay', color: 'title' },

  onClick: (e, el, s) => {
    e.stopPropagation()
    s.root.update({ notificationsPanelOpen: !s.root.notificationsPanelOpen })
  },

  Icon: { icon: 'bell', boxSize: 'A A' },

  UnreadBadge: {
    if: (el, s) => _unread(s.root) > 0,
    position: 'absolute',
    top: '0',
    right: '0',
    minWidth: 'Z',
    height: 'Z',
    padding: '0 V',
    round: 'C',
    background: 'red',
    color: 'white',
    fontSize: 'W',
    fontWeight: '700',
    lineHeight: '1',
    align: 'center center',
    pointerEvents: 'none',
    text: (el, s) => {
      const n = _unread(s.root)
      return n > 99 ? '99+' : String(n)
    }
  }
}

export const NotificationPanel = {
  extends: 'CommonCard',
  display: (el, s) => s.root?.notificationsPanelOpen ? 'flex' : 'none',
  position: 'absolute',
  top: 'B1',
  right: '0',
  width: '360px',
  maxHeight: '480px',
  overflow: 'hidden',
  padding: '0',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  boxShadow: '0 16px 48px -8px rgba(0,0,0,0.5)',
  zIndex: '3000',
  flow: 'y',

  onRender: (el) => {
    const handler = (e) => {
      if (!el.node?.contains(e.target) && !el.parent?.node?.contains(e.target)) {
        el.state.root.update({ notificationsPanelOpen: false })
        document.removeEventListener('mousedown', handler)
      }
    }
    setTimeout(() => document.addEventListener('mousedown', handler), 0)
    el._closeHandler = handler
  },
  onRemove: (el) => {
    if (el._closeHandler) document.removeEventListener('mousedown', el._closeHandler)
  },

  Header: {
    flow: 'x',
    align: 'center space-between',
    padding: 'Z1 A',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    Title: {
      tag: 'span',
      text: 'Notifications',
      fontWeight: '700',
      fontSize: 'Z1',
      color: 'title'
    },

    MarkAllBtn: {
      tag: 'button',
      type: 'button',
      text: 'Mark all read',
      background: 'none',
      borderWidth: '0',
      color: 'blue',
      fontSize: 'Y2',
      fontWeight: '600',
      cursor: 'pointer',
      padding: '0',
      if: (el, s) => _unread(s.root) > 0,
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('markAllNotificationsRead')
      }
    }
  },

  List: {
    overflow: 'auto',
    maxHeight: '420px',
    flow: 'y',

    Empty: {
      if: (el, s) => !(s.root?.notifications || []).length,
      padding: 'C A',
      textAlign: 'center',
      color: 'caption',
      fontSize: 'Z1',
      text: 'No notifications yet'
    },

    childrenAs: 'state',
    children: (el, s) => (s.root?.notifications || []).slice(0, 30),
    childExtends: {
      flow: 'x',
      gap: 'Z',
      padding: 'Z1 A',
      cursor: 'pointer',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      background: (el, s) => s.read ? 'transparent' : 'overlay',
      ':hover': { background: 'overlayStrong' },

      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('markNotificationRead', s.id)
        if (s.href) {
          el.state.root.update({ notificationsPanelOpen: false })
          let rootEl = el; while (rootEl && !rootEl.routes) rootEl = rootEl.parent
          if (rootEl) el.router(s.href, rootEl)
        }
      },

      UnreadDot: {
        if: (el, s) => !s.read,
        width: 'V',
        height: 'V',
        round: '50%',
        background: 'blue',
        flexShrink: '0',
        marginTop: 'Z'
      },

      TypeIcon: {
        Icon: {
          icon: (el, s) => ICON_BY_TYPE[s.parent?.state?.type] || 'star',
          boxSize: 'A',
          color: (el, s) => COLOR_BY_TYPE[s.parent?.state?.type] || 'caption'
        }
      },

      Body: {
        flex: '1',
        flow: 'y',
        gap: 'W',
        minWidth: '0',

        NTitle: {
          tag: 'span',
          fontSize: 'Z',
          fontWeight: '600',
          color: 'title',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => s.parent?.state?.title || ''
        },

        NBody: {
          if: (el, s) => s.parent?.state?.body,
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => s.parent?.state?.body || ''
        }
      },

      Time: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        flexShrink: '0',
        whiteSpace: 'nowrap',
        text: (el, s) => _timeAgo(s.createdAt)
      }
    }
  }
}
